import { isBoolean } from '@repo/lib/assertion';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { CheckIcon, XIcon } from 'lucide-react';

import type { CorrectIconProps } from '.';

export const correctIconVariants = cva(
  'grid shrink-0 cursor-pointer place-items-center rounded-ss-xs rounded-se-sm rounded-ee-xs rounded-es-sm transition-all duration-150 hover:scale-110 active:ring-2 active:ring-offset-1',
  {
    variants: {
      size: {
        sm: 'size-4 [&_svg]:size-3',
        default: 'size-5 [&_svg]:size-3.5',
        lg: 'size-6 [&_svg]:size-4',
      },
      answerState: {
        waiting: 'bg-gray text-gray-foreground active:ring-gray/50 text-[11px] font-semibold',
        partially: 'bg-warning text-warning-foreground active:ring-warning/50',
        correct: 'bg-success text-success-foreground active:ring-success/50',
        incorrect: 'bg-destructive text-destructive-foreground active:ring-destructive/50',
        error: 'bg-muted active:ring-muted/50 text-muted-foreground',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export function CorrectIcon(correctIconProps: CorrectIconProps) {
  const { answerState, className, size, ...props } = correctIconProps;

  const getVariantState = () => {
    if (answerState === 'waiting') return 'waiting';
    if (answerState === 'partially') return 'partially';
    if (!isBoolean(answerState)) return 'error';
    if (answerState === true) return 'correct';
    return 'incorrect';
  };

  const variantState = getVariantState();

  if (answerState === 'waiting') {
    return (
      <span aria-label="Odpowiedź oczekuje na ocenę" className={cn(correctIconVariants({ answerState: variantState, size }), className)} {...props}>
        ?
      </span>
    );
  }

  if (answerState === 'partially') {
    return (
      <span aria-label="Odpowiedź częściowo poprawna" className={cn(correctIconVariants({ answerState: variantState, size }), className)} {...props}>
        <CheckIcon />
      </span>
    );
  }

  if (!isBoolean(answerState)) {
    return (
      <span aria-label="Odpowiedź niepoprawna" className={cn(correctIconVariants({ answerState: variantState, size }), className)} {...props}>
        <XIcon />
      </span>
    );
  }

  if (answerState === true) {
    return (
      <span aria-label="Odpowiedź poprawna" className={cn(correctIconVariants({ answerState: variantState, size }), className)} {...props}>
        <CheckIcon />
      </span>
    );
  }

  return (
    <span aria-label="Odpowiedź niepoprawna" className={cn(correctIconVariants({ answerState: variantState, size }), className)} {...props}>
      <XIcon />
    </span>
  );
}
